/**
 * Production Batch List Page Component
 * Displays paginated list of production batches with filters and summary
 *
 * @author SAUBHAGYA Development Team
 * @since Story 5-5
 *
 * @description
 * Features:
 * - Summary cards (Total, Created, Processing, Manufactured, Ready for Sale)
 * - Filters (Status, Date range, Search)
 * - Data table with batch list
 * - Click to view details in modal
 * - Actions (Start, Complete, Ready for Sale)
 * - Create new batch button
 * - Pagination controls
 * - Loading and empty states
 *
 * @example
 * ```tsx
 * import { ProductionBatchListPage } from '@/pages/manufacturing/ProductionBatchListPage';
 *
 * <ProductionBatchListPage />
 * ```
 */

import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import {
  Package,
  PlayCircle,
  CheckCircle2,
  Clock,
  Search,
  X,
  Plus,
  Eye,
  Factory,
  ShoppingBag,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';

import {
  useProductionBatches,
  useProductionBatchSummary,
  useStartProductionBatch,
  ProductionBatchFilters,
} from '@/hooks/useProductionBatches';
import { toast } from '@/lib/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BatchStatusBadge } from '@/components/manufacturing/badges/BatchStatusBadge';
import { ProductionBatchDetailModal } from '@/components/manufacturing/modals/ProductionBatchDetailModal';
import { CreateProductionBatchModal } from '@/components/manufacturing/modals/CreateProductionBatchModal';
import { CompleteBatchModal } from '@/components/manufacturing/modals/CompleteBatchModal';
import { ProductionBatchDTO } from '@/services/manufacturing/manufacturingApi';

/**
 * Format date time
 */
function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'PP p');
  } catch {
    return dateString;
  }
}

/**
 * Format date only
 */
function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'PP');
  } catch {
    return dateString;
  }
}

/**
 * Get page number from paginated response (handles both 'page' and 'number' field names)
 */
function getPageNumber(data: any): number {
  return (data?.page ?? data?.number ?? 0);
}

/**
 * Summary Card Component
 */
interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  isLoading?: boolean;
}

function SummaryCard({ title, value, icon, description, isLoading }: SummaryCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Table Loading Skeleton
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Production Batch List Page Component
 */
export function ProductionBatchListPage() {
  // Filters state
  const [filters, setFilters] = useState<ProductionBatchFilters>({
    page: 0,
    size: 10,
    status: undefined,
    search: undefined,
  });

  const [searchInput, setSearchInput] = useState('');
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Modal states
  const [detailBatchId, setDetailBatchId] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [completeBatch, setCompleteBatch] = useState<ProductionBatchDTO | null>(null);
  const [startBatchConfirm, setStartBatchConfirm] = useState<ProductionBatchDTO | null>(null);

  // Fetch data
  const { data: batchesData, isLoading: isLoadingBatches } = useProductionBatches(filters);
  const { data: summary, isLoading: isLoadingSummary } = useProductionBatchSummary();

  // Start production mutation
  const startBatch = useStartProductionBatch();

  // Real-time search with debounce - updates filters as user types
  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      setFilters({
        ...filters,
        search: searchInput || undefined,
        page: 0,
      });
    }, 300); // 300ms debounce delay

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Handle filter changes
  const handleStatusChange = (value: string) => {
    setFilters({
      ...filters,
      status: value === 'all' ? undefined : value,
      page: 0,
    });
  };

  // No longer needed - search is now real-time via useEffect
  // Kept for backward compatibility if needed
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Real-time search already handles this via debounce
  };

  const handleClearSearch = () => {
    setSearchInput('');
    // Create a completely new filter object to ensure React Query detects the change
    setFilters({
      page: 0,
      size: filters.size,
      status: filters.status,
      search: undefined,
    });
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    // Create a completely new filter object to ensure React Query detects the change
    setFilters({
      page: newPage,
      size: filters.size,
      manufacturingUnitId: filters.manufacturingUnitId,
      status: filters.status,
      startDate: filters.startDate,
      endDate: filters.endDate,
      search: filters.search,
    });
  };

  // Handle view detail
  const handleViewDetail = (batchId: number) => {
    setDetailBatchId(batchId);
  };

  // Handle complete batch from detail modal
  const handleCompleteFromDetail = (batch: ProductionBatchDTO) => {
    setDetailBatchId(null);
    setCompleteBatch(batch);
  };

  // Handle start production
  const handleStartProduction = () => {
    if (!startBatchConfirm) return;

    startBatch.mutate(startBatchConfirm.id, {
      onSuccess: () => {
        toast.success(
          `Production Started - Batch ${startBatchConfirm.batchCode} is now in production.`
        );
        setStartBatchConfirm(null);
      },
      onError: (error: Error) => {
        toast.error(
          error.message || 'An error occurred while starting production.'
        );
      },
    });
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Production Batches</h1>
          <p className="text-muted-foreground">
            Manage and track production batch manufacturing
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Batch
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <SummaryCard
          title="Total Batches"
          value={summary?.totalBatches || 0}
          icon={<Package className="h-4 w-4 text-muted-foreground" />}
          description="All production batches"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Created"
          value={summary?.createdCount || 0}
          icon={<Clock className="h-4 w-4 text-slate-600" />}
          description="Ready to start"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Processing"
          value={summary?.processingCount || 0}
          icon={<PlayCircle className="h-4 w-4 text-blue-600" />}
          description="Currently manufacturing"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Manufactured"
          value={summary?.manufacturedCount || 0}
          icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
          description="Successfully completed"
          isLoading={isLoadingSummary}
        />
        <SummaryCard
          title="Ready for Sale"
          value={summary?.readyForSaleCount || 0}
          icon={<ShoppingBag className="h-4 w-4 text-emerald-600" />}
          description="Prepared for sale"
          isLoading={isLoadingSummary}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Status Filter */}
            <div className="flex-1">
              <Select
                value={filters.status || 'all'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="CREATED">Created</SelectItem>
                  <SelectItem value="PROCESSING">Processing</SelectItem>
                  <SelectItem value="MANUFACTURED">Manufactured</SelectItem>
                  <SelectItem value="READY_FOR_SALE">Ready for Sale</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search - Real-time as you type */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by batch code, product name, or batch ID... (searches as you type)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-8"
                />
                {searchInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1 h-7 w-7 p-0"
                    onClick={handleClearSearch}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Batches Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Production Batches
            {batchesData && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({batchesData.totalElements} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Batch Code</TableHead>
                  <TableHead>Product Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Input (kg)</TableHead>
                  <TableHead className="text-right">Output (kg)</TableHead>
                  <TableHead>Started At</TableHead>
                  <TableHead>Completed At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingBatches ? (
                  <TableSkeleton />
                ) : batchesData && batchesData.content.length > 0 ? (
                  batchesData.content.map((batch) => (
                    <TableRow key={batch.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell
                        className="font-mono text-sm"
                        onClick={() => handleViewDetail(batch.id)}
                      >
                        {batch.batchCode}
                      </TableCell>
                      <TableCell onClick={() => handleViewDetail(batch.id)}>
                        <div>
                          <p className="font-medium">{batch.dungProductName}</p>
                          <p className="text-xs text-muted-foreground">
                            <Factory className="inline h-3 w-3 mr-1" />
                            {batch.manufacturingUnitName}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell onClick={() => handleViewDetail(batch.id)}>
                        <BatchStatusBadge status={batch.status} />
                      </TableCell>
                      <TableCell
                        className="text-right font-medium"
                        onClick={() => handleViewDetail(batch.id)}
                      >
                        {batch.totalInputQuantityKg.toFixed(2)}
                      </TableCell>
                      <TableCell
                        className="text-right font-medium"
                        onClick={() => handleViewDetail(batch.id)}
                      >
                        {batch.outputQuantityKg
                          ? batch.outputQuantityKg.toFixed(2)
                          : '-'}
                      </TableCell>
                      <TableCell
                        className="text-sm text-muted-foreground"
                        onClick={() => handleViewDetail(batch.id)}
                      >
                        {formatDate(batch.startedAt)}
                      </TableCell>
                      <TableCell
                        className="text-sm text-muted-foreground"
                        onClick={() => handleViewDetail(batch.id)}
                      >
                        {formatDate(batch.completedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(batch.id)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {batch.status === 'CREATED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setStartBatchConfirm(batch)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <PlayCircle className="h-4 w-4 mr-1" />
                              Start
                            </Button>
                          )}
                          {batch.status === 'PROCESSING' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCompleteBatch(batch)}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Complete
                            </Button>
                          )}
                          {batch.status === 'MANUFACTURED' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetail(batch.id)}
                            >
                              <ShoppingBag className="h-4 w-4 mr-1" />
                              Ready for Sale
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Alert>
                        <AlertDescription>
                          {filters.status && !filters.search
                            ? `No batches found with status: ${filters.status.replace(/_/g, ' ')}`
                            : filters.search && !filters.status
                            ? `No batches found matching search: "${filters.search}"`
                            : filters.search && filters.status
                            ? `No batches found matching status: ${filters.status.replace(/_/g, ' ')} and search: "${filters.search}"`
                            : 'No production batches yet. Create your first batch to get started.'}
                        </AlertDescription>
                      </Alert>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {batchesData && batchesData.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {getPageNumber(batchesData) * (batchesData.size ?? 10) + 1} to{' '}
                {Math.min(
                  (getPageNumber(batchesData) + 1) * (batchesData.size ?? 10),
                  batchesData.totalElements ?? 0
                )}{' '}
                of {batchesData.totalElements ?? 0} batches
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(getPageNumber(batchesData) - 1)}
                  disabled={getPageNumber(batchesData) === 0}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: batchesData.totalPages }, (_, i) => i)
                    .filter(
                      (page) =>
                        page === 0 ||
                        page === batchesData.totalPages - 1 ||
                        Math.abs(page - getPageNumber(batchesData)) <= 1
                    )
                    .map((page, index, array) => (
                      <React.Fragment key={page}>
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2">...</span>
                        )}
                        <Button
                          variant={
                            page === getPageNumber(batchesData) ? 'default' : 'outline'
                          }
                          size="sm"
                          onClick={() => handlePageChange(page)}
                        >
                          {page + 1}
                        </Button>
                      </React.Fragment>
                    ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(getPageNumber(batchesData) + 1)}
                  disabled={getPageNumber(batchesData) >= batchesData.totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <ProductionBatchDetailModal
        open={detailBatchId !== null}
        onOpenChange={(open) => !open && setDetailBatchId(null)}
        batchId={detailBatchId}
        onComplete={handleCompleteFromDetail}
      />

      <CreateProductionBatchModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />

      <CompleteBatchModal
        open={completeBatch !== null}
        onOpenChange={(open) => !open && setCompleteBatch(null)}
        batch={completeBatch}
      />

      {/* Start Production Confirmation Dialog */}
      <AlertDialog
        open={startBatchConfirm !== null}
        onOpenChange={(open) => !open && setStartBatchConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start Production Cycle</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to start production for batch{' '}
              <span className="font-semibold text-foreground">
                {startBatchConfirm?.batchCode}
              </span>
              ?
              <br />
              <br />
              <span className="text-sm">
                Product: <strong>{startBatchConfirm?.dungProductName}</strong>
                <br />
                Input Quantity: <strong>{startBatchConfirm?.totalInputQuantityKg.toFixed(2)} kg</strong>
              </span>
              <br />
              <br />
              This action will change the batch status from{' '}
              <span className="font-medium text-slate-600">Created</span> to{' '}
              <span className="font-medium text-blue-600">Processing</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={startBatch.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStartProduction}
              disabled={startBatch.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {startBatch.isPending ? (
                <>
                  <PlayCircle className="mr-2 h-4 w-4 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayCircle className="mr-2 h-4 w-4" />
                  Start Production
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ProductionBatchListPage;
